import * as React from "react";

import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { renderMarkdown } from "@/components/app/markdown";

export interface MarkdownEditorProps {
  /** Controlled markdown value. */
  value: string;
  /** Called with the new markdown on every edit. */
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Render preview only, no edit tab (e.g. agent output viewer). */
  readOnly?: boolean;
  rows?: number;
  id?: string;
  name?: string;
  className?: string;
  /** Forwarded blur handler (useful for react-hook-form field registration). */
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
}

/**
 * Markdown editor with Write / Preview tabs (spec §18). v0.1 uses a plain
 * textarea plus a safe, dependency-free preview renderer. When `readOnly`, only
 * the rendered preview is shown.
 */
export const MarkdownEditor = React.forwardRef<
  HTMLTextAreaElement,
  MarkdownEditorProps
>(function MarkdownEditor(
  {
    value,
    onChange,
    placeholder = "Write markdown…",
    readOnly = false,
    rows = 10,
    id,
    name,
    className,
    onBlur,
  },
  ref,
) {
  const html = React.useMemo(() => renderMarkdown(value ?? ""), [value]);

  if (readOnly) {
    return (
      <MarkdownPreview html={html} className={className} empty={!value} />
    );
  }

  return (
    <Tabs defaultValue="write" className={cn("w-full", className)}>
      <TabsList>
        <TabsTrigger value="write">Write</TabsTrigger>
        <TabsTrigger value="preview">Preview</TabsTrigger>
      </TabsList>
      <TabsContent value="write">
        <Textarea
          ref={ref}
          id={id}
          name={name}
          value={value ?? ""}
          placeholder={placeholder}
          rows={rows}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          className="font-mono text-sm"
        />
      </TabsContent>
      <TabsContent value="preview">
        <MarkdownPreview html={html} empty={!value} />
      </TabsContent>
    </Tabs>
  );
});

function MarkdownPreview({
  html,
  empty,
  className,
}: {
  html: string;
  empty: boolean;
  className?: string;
}) {
  if (empty) {
    return (
      <div
        className={cn(
          "rounded-md border border-border p-3 text-sm text-muted-foreground",
          className,
        )}
      >
        Nothing to preview.
      </div>
    );
  }
  return (
    <div
      className={cn(
        "max-w-none space-y-2 rounded-md border border-border p-3 text-sm leading-relaxed",
        className,
      )}
      // Safe: renderMarkdown HTML-escapes all input before re-expanding a
      // limited, sanitized markdown subset (see markdown.ts).
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default MarkdownEditor;
